import React from "react";
import { X, Save, Pill, ClipboardList } from "lucide-react";

/**
 * MedicalNotesModal (minimal, consistent card style)
 * API unchanged.
 */
const MedicalNotesModal = ({
  open,
  appointment,
  onClose,
  onSave,
  medicalData,
  setMedicalData,
  newPrescription,
  setNewPrescription,
  onAddPrescription,
  onRemovePrescription,
  formatTime,
}) => {
  if (!open || !appointment) return null;

  const stop = (e) => e.stopPropagation();

  return (
    <div
      onClick={onClose}
      className="mnm-backdrop"
      role="presentation"
    >
      <style>{css}</style>

      <div
        onClick={stop}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mnm-title"
        className="mnm-dialog"
      >
        {/* Header */}
        <header className="mnm-header">
          <div className="mnm-title-wrap">
            <div className="mnm-title-icon" aria-hidden>
              <ClipboardList size={18} />
            </div>
            <div>
              <h2 id="mnm-title" className="mnm-title">Medical Record</h2>
              <p className="mnm-sub">
                Patient: <strong>{appointment.patientName}</strong> • {formatTime(appointment.scheduledTime)}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            aria-label="Close"
            className="mnm-icon-btn"
          >
            <X size={16} />
          </button>
        </header>

        {/* Body */}
        <div className="mnm-body">
          {/* Appointment summary */}
          <section className="mnm-summary card">
            <h3 className="mnm-section-title">Appointment Information</h3>
            <div className="mnm-summary-grid">
              <div><span className="mnm-k">Service:</span> {appointment.service}</div>
              <div><span className="mnm-k">Type:</span> {appointment.appointmentType}</div>
              <div><span className="mnm-k">Reason:</span> {appointment.reason || "—"}</div>
              <div><span className="mnm-k">Contact:</span> {appointment.patientInfo?.phone || "—"}</div>
            </div>
            {appointment.notes && (
              <div className="mnm-summary-notes">
                <span className="mnm-k">Patient Notes:</span> {appointment.notes}
              </div>
            )}
          </section>

          {/* 2-col layout */}
          <div className="mnm-grid">
            {/* Left */}
            <div className="mnm-col">
              <Field
                label="Symptoms & Chief Complaint"
                as="textarea"
                placeholder="Describe patient's symptoms and chief complaint…"
                value={medicalData.symptoms}
                onChange={(v) => setMedicalData((p) => ({ ...p, symptoms: v }))}
              />
              <Field
                label="Diagnosis"
                as="textarea"
                placeholder="Enter diagnosis and assessment…"
                value={medicalData.diagnosis}
                onChange={(v) => setMedicalData((p) => ({ ...p, diagnosis: v }))}
              />
              <Field
                label="Clinical Notes"
                as="textarea"
                placeholder="Additional clinical observations and notes…"
                value={medicalData.notes}
                onChange={(v) => setMedicalData((p) => ({ ...p, notes: v }))}
              />
            </div>

            {/* Right */}
            <div className="mnm-col">
              {/* Prescriptions */}
              <div className="mnm-block">
                <div className="mnm-block-head">
                  <div className="mnm-title-icon" aria-hidden><Pill size={16} /></div>
                  <label className="mnm-label">Prescriptions</label>
                </div>

                <div className="mnm-presc card">
                  <div className="mnm-presc-grid">
                    <Input
                      placeholder="Medication"
                      value={newPrescription.medication}
                      onChange={(v) => setNewPrescription((p) => ({ ...p, medication: v }))}
                    />
                    <Input
                      placeholder="Dosage (e.g., 500mg)"
                      value={newPrescription.dosage}
                      onChange={(v) => setNewPrescription((p) => ({ ...p, dosage: v }))}
                    />
                  </div>
                  <div className="mnm-presc-grid">
                    <Input
                      placeholder="Frequency (e.g., 2× daily)"
                      value={newPrescription.frequency}
                      onChange={(v) => setNewPrescription((p) => ({ ...p, frequency: v }))}
                    />
                    <Input
                      placeholder="Duration (e.g., 7 days)"
                      value={newPrescription.duration}
                      onChange={(v) => setNewPrescription((p) => ({ ...p, duration: v }))}
                    />
                  </div>
                  <Input
                    placeholder="Special instructions"
                    value={newPrescription.instructions}
                    onChange={(v) => setNewPrescription((p) => ({ ...p, instructions: v }))}
                  />
                  <div className="mnm-actions-right">
                    <button type="button" className="mnm-btn mnm-btn-primary" onClick={onAddPrescription}>
                      + Add Prescription
                    </button>
                  </div>
                </div>

                {medicalData.prescriptions.length > 0 && (
                  <div className="mnm-presc-list card">
                    {medicalData.prescriptions.map((p) => (
                      <div key={p.id} className="mnm-presc-row">
                        <div className="mnm-presc-info">
                          <div className="mnm-presc-title">{p.medication} — {p.dosage}</div>
                          <div className="mnm-presc-sub">{p.frequency} {p.duration ? `• ${p.duration}` : ""}</div>
                          {p.instructions && <div className="mnm-presc-ital">{p.instructions}</div>}
                        </div>
                        <button
                          className="mnm-icon-btn danger"
                          aria-label="Remove prescription"
                          onClick={() => onRemovePrescription(p.id)}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Field
                label="Follow-up Instructions"
                as="textarea"
                placeholder="Instructions for home care, red flags, return timeline…"
                value={medicalData.followUpInstructions}
                onChange={(v) => setMedicalData((p) => ({ ...p, followUpInstructions: v }))}
              />
              <Field
                label="Next Appointment Recommendation"
                placeholder="e.g., Follow-up in 2 weeks; return if symptoms worsen"
                value={medicalData.nextAppointment}
                onChange={(v) => setMedicalData((p) => ({ ...p, nextAppointment: v }))}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="mnm-footer">
          <button type="button" className="mnm-btn mnm-btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="mnm-btn mnm-btn-cta" onClick={onSave}>
            <Save size={16} />
            Save Record & Complete
          </button>
        </footer>
      </div>
    </div>
  );
};

/* ---------- Reusable tiny inputs (keeps the modal lean) ---------- */

const Input = ({ value, onChange, placeholder }) => (
  <input
    className="mnm-input"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
  />
);

const Field = ({ label, value, onChange, placeholder, as = "input" }) => (
  <div className="mnm-field">
    <label className="mnm-label">{label}</label>
    {as === "textarea" ? (
      <textarea
        className="mnm-textarea"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    ) : (
      <Input value={value} onChange={onChange} placeholder={placeholder} />
    )}
  </div>
);

/* ---------- Scoped CSS (minimal, neutral, responsive) ---------- */

const css = `
:root{
  --bg: #ffffff;
  --text: #111827;
  --muted: #6b7280;
  --line: #e5e7eb;
  --subtle: #f3f4f6;
  --card: #ffffff;
  --cardLine: #e5e7eb;

  --radius: 12px;
  --pad: 16px;

  --primary: #0ea5e9; /* sky-500 */
  --primary-strong: #0284c7;
  --green: #10b981;
  --green-strong: #059669;
  --danger: #ef4444;
}

/* Backdrop */
.mnm-backdrop{
  position: fixed;
  inset: 0;
  background: rgba(17,24,39,.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  z-index: 1000;
}

/* Dialog */
.mnm-dialog{
  background: var(--bg);
  color: var(--text);
  width: min(880px, 100%);
  max-height: 90vh;
  border: 1px solid var(--line);
  border-radius: 14px;
  box-shadow: 0 10px 40px rgba(0,0,0,.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Header */
.mnm-header{
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid var(--line);
  background: #fafafa;
}
.mnm-title-wrap{ display:flex; align-items:center; gap:10px; }
.mnm-title-icon{
  width: 28px; height: 28px;
  border-radius: 8px;
  display:grid; place-items:center;
  color: var(--green-strong);
  background: #ecfdf5;
  border: 1px solid #d1fae5;
}
.mnm-title{ margin:0; font-weight:700; font-size:16px; }
.mnm-sub{ margin:2px 0 0; font-size:12px; color: var(--muted); }

/* Body */
.mnm-body{ padding: 16px; overflow: auto; }

/* Footer */
.mnm-footer{
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding: 12px 16px;
  border-top: 1px solid var(--line);
  background: #fafafa;
}

/* Cards inside (summary, lists) */
.card{
  background: var(--card);
  border: 1px solid var(--cardLine);
  border-radius: 10px;
}

/* Summary */
.mnm-summary{ padding: 12px; }
.mnm-section-title{
  margin:0 0 10px 0;
  font-size: 13px; font-weight: 700;
  color: #0f172a;
}
.mnm-summary-grid{
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  font-size: 13px;
}
.mnm-summary-notes{ margin-top: 8px; font-size: 13px; }
.mnm-k{ color: #334155; font-weight: 600; }

/* Layout grid */
.mnm-grid{
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 16px;
}
.mnm-col{ display: flex; flex-direction: column; gap: 12px; }

/* Fields */
.mnm-field{ display: grid; gap: 8px; }
.mnm-label{ font-size: 13px; font-weight: 600; color: #374151; }

.mnm-input, .mnm-textarea{
  width: 100%;
  font: inherit;
  font-size: 14px;
  color: var(--text);
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 8px;
  padding: 10px 12px;
  box-sizing: border-box;
  transition: border-color .12s ease, box-shadow .12s ease;
}
.mnm-input:focus, .mnm-textarea:focus{
  outline: none;
  border-color: #93c5fd;
  box-shadow: 0 0 0 3px rgba(147,197,253,.35);
}
.mnm-textarea{ min-height: 96px; resize: vertical; }

/* Prescriptions */
.mnm-block{ display: grid; gap: 10px; }
.mnm-block-head{ display:flex; align-items:center; gap:8px; }
.mnm-presc{ display: grid; gap: 8px; padding: 12px; }
.mnm-presc-grid{ display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.mnm-actions-right{ display:flex; justify-content:flex-end; }

/* Prescriptions list */
.mnm-presc-list{ margin-top: 8px; }
.mnm-presc-row{
  display: flex; align-items: flex-start; gap: 8px;
  padding: 10px 12px; border-bottom: 1px solid #f1f5f9;
}
.mnm-presc-row:last-child{ border-bottom: 0; }
.mnm-presc-info{ flex: 1; min-width: 0; }
.mnm-presc-title{ font-weight: 700; font-size: 13px; color:#0f172a; margin-bottom: 2px; }
.mnm-presc-sub{ font-size: 12px; color: #6b7280; margin-bottom: 2px; }
.mnm-presc-ital{ font-size: 12px; color: #6b7280; font-style: italic; }

/* Buttons */
.mnm-btn{
  height: 38px;
  padding: 0 14px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 13px;
  border: 1px solid transparent;
  background: #f8fafc;
  color: #0f172a;
  cursor: pointer;
  display: inline-flex; align-items: center; gap: 8px;
  transition: box-shadow .12s ease, transform .12s ease, background .12s ease, border-color .12s ease;
}
.mnm-btn:hover{ transform: translateY(-1px); }
.mnm-btn:focus{ outline: none; box-shadow: 0 0 0 3px rgba(147,197,253,.35); }

.mnm-btn-ghost{
  background: #fff;
  border-color: var(--line);
}
.mnm-btn-ghost:hover{ background: #f3f4f6; }

.mnm-btn-primary{
  background: #111827;
  color: #fff;
}
.mnm-btn-primary:hover{ background: #0b1220; }

.mnm-btn-cta{
  background: linear-gradient(0deg, var(--green-strong), var(--green-strong));
  color: #fff;
  border-color: var(--green-strong);
}
.mnm-btn-cta:hover{ background: var(--green); border-color: var(--green); }

.mnm-icon-btn{
  height: 34px; width: 34px;
  border-radius: 8px;
  border: 1px solid var(--line);
  background: #fff;
  display: grid; place-items: center;
  cursor: pointer;
  transition: background .12s ease, border-color .12s ease, transform .12s ease;
}
.mnm-icon-btn:hover{ background:#f3f4f6; transform: translateY(-1px); }
.mnm-icon-btn.danger{ color: var(--danger); border-color: #fee2e2; background:#fff; }
.mnm-icon-btn.danger:hover{ background:#fff5f5; }

/* Responsive */
@media (max-width: 900px){
  .mnm-grid{ grid-template-columns: 1fr; }
}
@media (max-width: 520px){
  .mnm-header, .mnm-footer{ padding: 10px 12px; }
  .mnm-body{ padding: 12px; }
}
`;

export default React.memo(MedicalNotesModal);
